/** UI-facing reactive state. The GameClient (imperative, Pixi/engine side) pushes
 * telemetry in; React components read it. Keeps the render loop decoupled from
 * React's render cycle — we only touch the store a few times per second. */
import { create } from 'zustand';
import type { Biome, TerrainType } from '@settlers/shared';

export interface HoveredTile {
  x: number;
  y: number;
  terrain: TerrainType;
  biome: Biome;
  buildable: boolean;
  elevation: number;
}

export interface Telemetry {
  fps: number;
  tps: number;
  tick: number;
  entityCount: number;
  zoom: number;
  chunks: number;
  visibleChunks: number;
  workers: number;
  roads: number;
  trips: number;
}

/** How pointer input is interpreted. */
export type InteractionMode = 'camera' | 'road' | 'spawn';

interface GameStore {
  ready: boolean;
  seed: number;
  paused: boolean;
  mode: InteractionMode;
  telemetry: Telemetry;
  hovered: HoveredTile | null;
  setReady: (ready: boolean) => void;
  setSeed: (seed: number) => void;
  setPaused: (paused: boolean) => void;
  setMode: (mode: InteractionMode) => void;
  setTelemetry: (t: Telemetry) => void;
  setHovered: (h: HoveredTile | null) => void;
}

export const useGameStore = create<GameStore>((set) => ({
  ready: false,
  seed: 0,
  paused: false,
  mode: 'camera',
  telemetry: {
    fps: 0,
    tps: 0,
    tick: 0,
    entityCount: 0,
    zoom: 1,
    chunks: 0,
    visibleChunks: 0,
    workers: 0,
    roads: 0,
    trips: 0,
  },
  hovered: null,
  setReady: (ready): void => set({ ready }),
  setSeed: (seed): void => set({ seed }),
  setPaused: (paused): void => set({ paused }),
  setMode: (mode): void => set({ mode }),
  setTelemetry: (telemetry): void => set({ telemetry }),
  setHovered: (hovered): void => set({ hovered }),
}));
